import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import {
  taxCreditApplicationRequests,
  taxCreditApplicationRequestsIsMobile,
} from "my-hr/configs/my-hr-table-defs";
import MyTaxCreditApplications from "my-tax/components/tax-credit-applications/tax-credit-applications";
import useSelectedTaxDeclaration from "my-tax/hooks/use-selected-tax-declaration";
import useTaxDeclarationList from "my-tax/hooks/use-tax-declaration-list";
import { OcCard, OcDataTable, OcPageTransition } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import { useOcButtonGroup, useOcTableState, useOcProperty } from "oc/hooks";

interface MyTaxApplicationsDataPanelProps {
  label: string;
  employee: any;
  isMobile: boolean;
}

export const MyTaxApplicationsDataPanel = ({
  label,
  employee,
  isMobile,
}: MyTaxApplicationsDataPanelProps) => {
  const {
    selected,
    setSelected,
    reset: resetSelectedState,
  } = useSelectedTaxDeclaration();

  const [type, setType] = useState<"all" | "requested" | "invalid">("all");
  const [selectedRowId, setSelectedRowId] = useState<string | number | undefined>(undefined);

  const { t } = useTranslation();

  const { data: taxApplicationSave } = useOcProperty({
    resource: "system",
    key: "taxApplicationSaveEnabled",
    fetchPolicy: "network-only",
  });
  const taxApplicationSaveEnabled = taxApplicationSave === "true";

  const tableId = "taxDeclartionListTable";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    pageSize: 5,
  });

  const { data: queryData, loading: listLoading, error } = useTaxDeclarationList({ tableData, type });

  // Cache the data in state - only update when we have new data with actual rows
  const [cachedData, setCachedData] = useState<typeof queryData | null>(null);
  useEffect(() => {
    if (queryData?.rows?.length) {
      setCachedData(queryData);
    }
  }, [queryData]);

  // Use cached data if current query data has no rows
  const data = queryData?.rows?.length ? queryData : (cachedData ?? queryData);

  // Only show loading if we have absolutely no data (neither current nor cached)
  const loading = listLoading && !queryData?.rows?.length && !cachedData?.rows?.length;

  const buttonsDef = ["OC_REQUESTED_PAST", "MY_TAX_INVALID", "OC_ALL"];
  const { OcButtonGroup, selectedButton, handleButtonChange } = useOcButtonGroup({
    id: "MyHrApplicationsDataPanel",
    buttonsDef,
  });

  const originalonSelectRow = (row: any) => {
    const data = {
      editable: false,
      tableId: row.tableId,
      year: row.year,
      type: row.type,
      state: row.state,
    };
    setSelected(data);
  };

  const handleMobileAction = ({ row }: { action: string; row: any }) => {
    originalonSelectRow(row);
  };

  const handleSelectRow = (row: any) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === row._id ? undefined : row._id);
    } else {
      originalonSelectRow(row);
    }
  };

  const tableDef = isMobile
    ? taxCreditApplicationRequestsIsMobile
    : taxCreditApplicationRequests;

  const filteredTableDef = {
    id: taxCreditApplicationRequests?.id,
    columns: taxCreditApplicationRequests?.columns?.filter(
      (column) =>
        !taxCreditApplicationRequestsIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  useEffect(() => {
    resetSelectedState();
    handleButtonChange(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedButton === "OC_REQUESTED_PAST") {
      setType("requested");
    } else if (selectedButton === "MY_TAX_INVALID") {
      setType("invalid");
    } else if (selectedButton === "OC_ALL") {
      setType("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedButton]);

  return (
    <OcPageTransition>
      <Box>
        {!selected?.type && (
          <OcCard
            label={t("MY_TAX_MY_APPLICATIONS")}
            loading={loading}
            error={!!error}
          >
            <Box
              id="button-group"
              display="flex"
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
              padding="18px 0px"
            >
              <OcButtonGroup />
            </Box>
            <Box paddingTop="32px">
              <OcDataTable
                data={data}
                loading={loading && data?.rows?.length === 0 ? true : false}
                tableDef={tableDef}
                tableData={tableData}
                setTableData={setTableData}
                pagination
                onSelectRow={handleSelectRow}
                selectedRowId={selectedRowId}
                filteredTableDef={filteredTableDef}
                handleMoreButton={handleMobileAction}
              />
            </Box>
          </OcCard>
        )}

        {/* Részletező panel */}
        <MyTaxCreditApplications
          label={label}
          employee={employee}
          taxApplicationSaveEnabled={taxApplicationSaveEnabled}
          selected={selected}
          setSelected={setSelected}
          resetSelectedState={resetSelectedState}
          requestState={type}
        />
      </Box>
    </OcPageTransition>
  );
};

export default MyTaxApplicationsDataPanel;
